import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './Sheet.css';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
let openSheets = 0;

/**
 * Bottom sheet on phones, centred dialog from 768px up. The one overlay surface for the student product
 * (profile, avatar picker, filters, confirmations…) — replaces desktop-style popups.
 *
 *  - rendered in a portal, so no transformed/overflow-hidden ancestor can trap or clip it
 *  - focus moves in on open, is trapped while open, and returns to the opener on close; Esc closes
 *  - page scroll is locked while any sheet is open
 *  - respects the safe areas (notch, gesture bar) and shrinks/scrolls when the on-screen keyboard opens
 *  - `footer` stays pinned (primary actions never scroll away)
 */
export default function Sheet({ open, onClose, title, children, footer, dismissible = true, hideClose = false, size = 'md', label }) {
  const id = useId();
  const cardRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    openSheets += 1;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => {
      const el = cardRef.current;
      if (!el) return;
      (el.querySelector('[data-autofocus]') || el.querySelector(FOCUSABLE) || el).focus?.();
    }, 30);
    return () => {
      clearTimeout(t);
      openSheets -= 1;
      if (openSheets <= 0) { openSheets = 0; document.body.style.overflow = ''; }
      const opener = openerRef.current;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [open]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && dismissible) { e.stopPropagation(); onClose?.(); return; }
    if (e.key !== 'Tab' || !cardRef.current) return;
    const items = Array.from(cardRef.current.querySelectorAll(FOCUSABLE));
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, [dismissible, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="z-sheet-root" onKeyDown={onKeyDown}>
      <div className="z-sheet-backdrop" onClick={dismissible ? onClose : undefined} aria-hidden="true" />
      <div
        className={`z-sheet z-sheet--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${id}-title` : undefined}
        aria-label={!title ? label : undefined}
        ref={cardRef}
        tabIndex={-1}
      >
        <div className="z-sheet-grip" aria-hidden="true" />
        {(title || (dismissible && !hideClose)) && (
          <div className="z-sheet-head">
            {title ? <h2 className="z-sheet-title" id={`${id}-title`}>{title}</h2> : <span />}
            {dismissible && !hideClose && (
              <button type="button" className="z-sheet-close" onClick={onClose} aria-label="Close">
                <Icon name="close" size={20} />
              </button>
            )}
          </div>
        )}
        <div className="z-sheet-body">{children}</div>
        {footer && <div className="z-sheet-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
